import React, { useState, useEffect } from 'react';
import { 
    Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, Box, Tooltip, Switch,
    DialogActions, Button, CircularProgress, IconButton,
    Dialog, DialogTitle, DialogContent, TextField,
    FormControlLabel, Typography, MenuItem, Select
} from '@mui/material';
import API_BASE_URL from '../../config';
import { Fab } from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';
import './AdminSetup.css';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [packages, setPackages] = useState([]);


    const fetchUsers = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        const fetchPackages = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/admin/packages`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                const data = await response.json();
                setPackages(data);
            } catch (err) {
                console.error('Failed to load packages', err);
            }
        };
        fetchPackages();
    }, []);

    const handlePackageChange = async (userId, newPackageId) => {
        try {
            await fetch(`${API_BASE_URL}/admin/users/${userId}/package`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ package_id: newPackageId })
            });
            
            setUsers(prevUsers => prevUsers.map(user => 
                user.id === userId ? { ...user, package_id: newPackageId } : user
            ));
        } catch (err) {
            setError('Failed to update package');
        }
    };

    const handleSubmit = async (userData) => {
        setIsProcessing(true);
        try {
            const method = selectedUser ? 'PUT' : 'POST';
            // Add API_BASE_URL to the endpoint
            const url = selectedUser ? 
                `${API_BASE_URL}/admin/users/${selectedUser.id}` : 
                `${API_BASE_URL}/admin/users`;

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(userData)
            });

            if (!response.ok) throw new Error('Operation failed');
            fetchUsers();
            handleCloseDialog();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDelete = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                fetchUsers();
            } catch (err) {
                setError('Delete operation failed');
            }
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
    };

    const columns = [
        { field: 'username', headerName: 'Username', width: 150 },
        { field: 'first_name', headerName: 'First Name', width: 120 },
        { field: 'last_name', headerName: 'Last Name', width: 120 },
        { field: 'org_name', headerName: 'Organization', width: 180 },
        { field: 'org_email', headerName: 'Email', width: 200 },
        { field: 'phone_number', headerName: 'Phone', width: 130 },
        { 
            field: 'active', 
            headerName: 'Active', 
            width: 100,
            renderCell: (params) => (
                <Switch checked={params.value} disabled />
            )
        },
        { field: 'package', headerName: 'Package', width: 180 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            renderCell: (params) => (
                <>
                    <Tooltip title="Edit">
                        <IconButton onClick={() => {
                            setSelectedUser(params.row);
                            setOpenDialog(true);
                        }}>
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton onClick={() => handleDelete(params.row.id)}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </>
            )
        }
    ];

    return (
        <Box sx={{ p: 3, height: '100vh', backgroundColor: '#ffffff' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" sx={{ color: 'black' }}>User Management</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<Add />}
                    onClick={() => setOpenDialog(true)}
                >
                    Add User
                </Button>
            </Box>

            {error && <Typography color="error">{error}</Typography>}

            <Box sx={{ height: '80%', width: '100%' }}>
                <TableContainer component={Paper} sx={{ backgroundColor: '#ececec', color: 'black' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Username</TableCell>
                                <TableCell>First Name</TableCell>
                                <TableCell>Last Name</TableCell>
                                <TableCell>Organization</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Active</TableCell>
                                <TableCell>Package</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.username}</TableCell>
                                    <TableCell>{user.first_name}</TableCell>
                                    <TableCell>{user.last_name}</TableCell>
                                    <TableCell>{user.org_name}</TableCell>
                                    <TableCell>{user.org_email}</TableCell>
                                    <TableCell>{user.phone_number}</TableCell>
                                    <TableCell>
                                        <Switch checked={user.active} disabled />
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            value={user.package_id || ''}
                                            onChange={(e) => handlePackageChange(user.id, e.target.value)}
                                            sx={{ 
                                                color: 'white',
                                                '.MuiOutlinedInput-notchedOutline': { borderColor: '#000000' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#000000' }
                                            }}
                                        >
                                            {packages.map(pkg => (
                                                <MenuItem key={pkg.id} value={pkg.id}>
                                                    {pkg.package_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => {
                                            setSelectedUser(user);
                                            setOpenDialog(true);
                                        }}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Fab 
            color="primary" 
            aria-label="add"
            sx={{ 
                position: 'fixed',
                bottom: 16,
                right: 16,
                bgcolor: '#002b57',
                '&:hover': {
                    bgcolor: '#000f1f'
                }
            }}
            onClick={() => setOpenDialog(true)}
        >
            <Add />
        </Fab>

        <UserDialog
            open={openDialog}
            onClose={handleCloseDialog}
            user={selectedUser}
            onSubmit={handleSubmit}
            isProcessing={isProcessing}
        />
        </Box>
    );
};

const UserDialog = ({ open, onClose, user, onSubmit, isProcessing }) => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        control_password: '',
        admin_password: '',
        org_name: '',
        org_email: '',
        notification_email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        active: true,
        dark_mode: true,
        data_user_enabled: false
    });

    useEffect(() => {
        if (user) {
            setFormData(user);
        } else {
            setFormData({
                username: '',
                password: '',
                control_password: '',
                admin_password: '',
                org_name: '',
                org_email: '',
                notification_email: '',
                first_name: '',
                last_name: '',
                phone_number: '',
                active: true,
                dark_mode: true,
                data_user_enabled: false
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ bgcolor: '#ffffff', color: 'black' }}>
                {user ? 'Edit User' : 'Create New User'}
            </DialogTitle>
            <DialogContent sx={{ bgcolor: '#ffffff', color: 'black' }}>
                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 2 }}>
                        <TextField
                            label="Username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required={!user}
                        />
                        <TextField
                            label="Control Password"
                            name="control_password"
                            value={formData.control_password}
                            onChange={handleChange}
                            required={!user}
                        />
                        <TextField
                            label="Admin Password"
                            name="admin_password"
                            value={formData.admin_password}
                            onChange={handleChange}
                            required={!user}
                        />
                        <TextField
                            label="First Name"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            required
                        />
                        <TextField
                            label="Last Name"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            required
                        />
                        <TextField
                            label="Organization"
                            name="org_name"
                            value={formData.org_name}
                            onChange={handleChange}
                            required
                        />
                        <TextField
                            label="Organization Email"
                            name="org_email"
                            type="email"
                            value={formData.org_email}
                            onChange={handleChange}
                            required
                        />
                        <TextField
                            label="Notification Email"
                            name="notification_email"
                            type="email"
                            value={formData.notification_email}
                            onChange={handleChange}
                            required
                        />
                        <TextField
                            label="Phone Number"
                            name="phone_number"
                            value={formData.phone_number}
                            onChange={handleChange}
                            required
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleChange}
                                />
                            }
                            label="Active"
                            sx={{ 
                                color: '#000', 
                                '& .MuiFormControlLabel-label': {
                                    color: '#000 !important', 
                                }
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    name="dark_mode"
                                    checked={formData.dark_mode}
                                    onChange={handleChange}
                                />
                            }
                            label="Dark Mode"
                            sx={{ 
                                color: '#000', 
                                '& .MuiFormControlLabel-label': {
                                    color: '#000 !important', 
                                }
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    name="data_user_enabled"
                                    checked={formData.data_user_enabled}
                                    onChange={handleChange}
                                />
                            }
                            label="Data User Enabled"
                            sx={{ 
                                color: '#000', 
                                '& .MuiFormControlLabel-label': {
                                    color: '#000 !important', 
                                }
                            }}
                        />
                    </Box>
                    <DialogActions sx={{ pt: 3 }}>
                        <Button onClick={onClose} color="inherit">Cancel</Button>
                        <Button 
                            type="submit" 
                            variant="contained"
                            disabled={isProcessing}
                        >
                            {isProcessing ? <CircularProgress size={24} /> : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AdminUsers;